import { useEffect, useState, useSyncExternalStore } from 'react';
import type { OperatorActivity } from '@erp/rpc';
import {
  acquireOperatorStream,
  type OperatorStreamHandle,
} from './operatorSessionStreamStore';

/**
 * Live operator session stream over canonical AG-UI (AI-244 — see
 * docs/operator/operator-live-stream.md).
 *
 * Drives a real `@ag-ui/client` agent (OperatorAgent over the oRPC stream) and
 * surfaces its client-managed state — which is the operator panel
 * (`OperatorActivity`: status + liveViewUrl + messages + screenshots), the same
 * shape `useSessionStatus` returns, so the viewer renders it unchanged. The AG-UI
 * client applies STATE_SNAPSHOT/STATE_DELTA for us. `unavailable` is set when the
 * run can't be streamed from this replica → the caller falls back to the poll.
 *
 * The agent itself lives in `operatorSessionStreamStore`, ref-counted per session, so several
 * components watching one desktop share ONE stream instead of opening one each.
 */

export interface OperatorSessionStream {
  activity: OperatorActivity | null;
  finished: boolean;
  unavailable: boolean;
  error: string | null;
}

const INITIAL: OperatorSessionStream = {
  activity: null,
  finished: false,
  unavailable: false,
  error: null,
};

/** The inactive store: nothing to subscribe to, and a snapshot that is stable by identity. */
const IDLE = {
  subscribe: () => () => undefined,
  getSnapshot: () => INITIAL,
};

export interface UseOperatorSessionStreamOptions {
  enabled: boolean;
  organizationId: string | undefined;
  sessionId: string | undefined;
  threadId?: string;
}

export function useOperatorSessionStream({
  enabled,
  organizationId,
  sessionId,
  threadId,
}: UseOperatorSessionStreamOptions): OperatorSessionStream {
  const active = enabled && Boolean(organizationId) && Boolean(sessionId);

  // The stream is an external system, so joining it is an effect — as it always was. Holding the
  // handle in state (rather than a ref) is what re-renders the component once the subscription
  // exists; `useSyncExternalStore` then keeps it current.
  const [handle, setHandle] = useState<OperatorStreamHandle | null>(null);

  useEffect(() => {
    if (!active || !organizationId || !sessionId) {
      setHandle(null);
      return;
    }
    const joined = acquireOperatorStream(organizationId, sessionId, threadId);
    setHandle(joined);
    return () => {
      setHandle(null);
      joined.release();
    };
  }, [active, organizationId, sessionId, threadId]);

  return useSyncExternalStore(
    handle?.subscribe ?? IDLE.subscribe,
    handle?.getSnapshot ?? IDLE.getSnapshot,
  );
}
